import type { FieldVisitPlace } from '../../field-operations/types/FieldOperations';

export type TaskStatus = string;
export type TaskAiRunStatus = 'none' | 'queued' | 'running' | 'succeeded' | 'failed' | 'needsReview';
export const AI_TASK_ASSIGNEE = 'ai';
export const AI_ASSISTANT_ID = '00000000-0000-0000-0000-000000000001';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TaskRecurrence = 'doesNotRepeat' | 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface TaskStatusDefinition {
  id: string;
  code: string;
  name: string;
  color: string;
  displayOrder: number;
  isDefault: boolean;
  isCompleted: boolean;
  requiresAssignee: boolean;
  transitionToCodes: string[];
}

export interface TaskCategoryItem {
  id: string;
  name: string;
  code?: string;
  parentCategoryId?: string;
}

export interface TaskLabelItem {
  id: string;
  name: string;
}

export interface TaskCommentItem {
  id: string;
  taskId: string;
  content: string;
  createdBy: string;
  createdAt: string;
}

export interface TaskCommentMentionInput {
  mentionedEmployeeId: string;
  mentionText: string;
}

export interface TaskActivityItem {
  id: string;
  taskId: string;
  type: string;
  message: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  createdBy: string;
  createdAt: string;
}

export interface TaskAttachmentItem {
  id: string;
  taskId: string;
  taskCommentId?: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  mediaUrl: string;
  createdBy: string;
  createdAt: string;
}

export interface SubtaskItem {
  id: string;
  taskKey: string;
  title: string;
  status: TaskStatus;
  assignedToUserId?: string;
}

export interface TaskConversationLinkItem {
  conversationId: string;
  conversationMessageId?: string;
  linkedByEmployeeId: string;
  linkedAt: string;
  isPrimary: boolean;
}

export interface TaskRelatedTaskLinkItem {
  relatedTaskId: string;
  relatedTaskKey: string;
  relatedTaskTitle: string;
  relatedTaskStatus: TaskStatus;
  linkedByEmployeeId: string;
  linkedAt: string;
}

export interface TaskItem {
  id: string;
  title: string;
  description?: string;
  dueDate?: string;
  dueTime?: string;
  recurrence: TaskRecurrence;
  priority: TaskPriority;
  status: TaskStatus;
  category?: TaskCategoryItem;
  contactId?: string;
  assetId?: string;
  assignedToUserId?: string;
  assignedToAi?: boolean;
  aiRunStatus?: TaskAiRunStatus;
  aiAttemptCount?: number;
  aiLastError?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  taskKey: string;
  keyNumber: number;
  parentTaskId?: string;
  parentTaskKey?: string;
  labels?: TaskLabelItem[];
  subtasks: SubtaskItem[];
  conversationLinks: TaskConversationLinkItem[];
  relatedTasks: TaskRelatedTaskLinkItem[];
  visitPlace?: FieldVisitPlace;
}

export interface TaskListQuery {
  page?: number;
  pageSize?: number;
  status?: TaskStatus;
  priority?: TaskPriority;
  categoryId?: string;
  contactId?: string;
  assetId?: string;
  assignedToUserId?: string;
  assignedToAiOnly?: boolean;
  unassignedOnly?: boolean;
  dueDateFrom?: string;
  dueDateTo?: string;
  searchText?: string;
}

export interface PagedTaskResult {
  items: TaskItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface CreateTaskRequest {
  title: string;
  description?: string;
  dueDate?: string;
  dueTime?: string;
  recurrence: TaskRecurrence;
  priority: TaskPriority;
  categoryId?: string;
  contactId?: string;
  assetId?: string;
  assignedToUserId?: string;
  assignedToAi?: boolean;
  labels?: string[];
  visitPlace?: FieldVisitPlace;
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  dueDate?: string;
  dueTime?: string;
  recurrence?: TaskRecurrence;
  priority?: TaskPriority;
  status?: TaskStatus;
  categoryId?: string;
  contactId?: string;
  assetId?: string;
  assignedToUserId?: string;
  assignedToAi?: boolean;
  requeueAiRun?: boolean;
  labels?: string[];
  visitPlace?: FieldVisitPlace;
}
